import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import colours from "../../constants/colours";
import { FaBook, FaBrain, FaLightbulb, FaTools } from "react-icons/fa";

const CompetencyMap = ({
  sessionId,
  currentAbility,
  toggledCompetencies,
  onToggleCompetency,
  competenciesByAbility,
  setHasUnsavedChanges,
  className
}) => {
  console.log("CompetencyMap rendering with:", {
    currentAbility: currentAbility?.name,
    toggledCompetenciesCount: toggledCompetencies?.length,
    competenciesByAbility: competenciesByAbility ? Object.keys(competenciesByAbility) : null
  });

  // Debug the actual competencies in each category
  if (competenciesByAbility) {
    console.log("Skills competencies:", competenciesByAbility.skills);
    console.log("Knowledge competencies:", competenciesByAbility.knowledge);
    console.log("Mindset competencies:", competenciesByAbility.mindset);
    console.log("Tool competencies:", competenciesByAbility.tool);
  }

  const [scale, setScale] = useState(0.6);
  const [loading, setLoading] = useState(false);

  // Update loading state when ability or competencies change
  useEffect(() => {
    setLoading(!currentAbility || !competenciesByAbility);
  }, [currentAbility, competenciesByAbility]);

  const handleCompetencyClick = async (competencyId, e) => {
    e.stopPropagation();
    console.log("CompetencyMap: Competency clicked:", competencyId);
    onToggleCompetency(competencyId);
    setHasUnsavedChanges(true);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const scaleFactor = e.deltaY > 0 ? 0.95 : 1.05;
    setScale((prevScale) =>
      Math.min(Math.max(prevScale * scaleFactor, 0.5), 2)
    );
  };

  const getPositionStyles = (position) => {
    const radius = 240;
    const diagonalRadius = radius * Math.sqrt(2);
    let x, y, transform;

    switch (position) {
      case "top":
        x = 0;
        y = -radius;
        transform = "translate(-50%, -100%)";
        break;
      case "right":
        x = diagonalRadius;
        y = 0;
        transform = "translate(0%, -50%)";
        break;
      case "bottom":
        x = 0;
        y = radius;
        transform = "translate(-50%, 0%)";
        break;
      case "left":
        x = -diagonalRadius;
        y = 0;
        transform = "translate(-100%, -50%)";
        break;
      default:
        x = 0;
        y = 0;
        transform = "translate(-50%, -50%)";
    }

    return {
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: `translate(calc(${x}px), calc(${y}px)) ${transform}`,
    };
  };

  // Helper function to extract competency data from API response
  const extractCompetencies = (categoryItems) => {
    if (!categoryItems || !Array.isArray(categoryItems)) return [];

    // Check if the items have a nested competency property (from session responses)
    if (categoryItems.length > 0 && categoryItems[0].competency) {
      return categoryItems.map(item => ({
        id: item.competency_id || item.competency.id,
        name: item.competency.name,
        description: item.competency.description,
        category: item.competency.category
      }));
    }

    // Direct competency objects
    return categoryItems;
  };

  const CategoryGrid = ({ title, items, position, icon }) => {
    const gridStyles = getPositionStyles(position);
    const isVertical = position === "left" || position === "right";

    // Process items to ensure they have the right format
    const processedItems = extractCompetencies(items);

    console.log(`Rendering ${title} grid with ${processedItems?.length || 0} items:`, processedItems);

    // If items is undefined or empty, show a message
    if (!processedItems || processedItems.length === 0) {
      return (
        <div
          className={`glassEffect rounded-lg shadow-lg p-4 z-50 ${isVertical ? "w-[420px]" : "w-[600px]"
            }`}
          style={gridStyles}
        >
          <div className="flex w-full justify-center gap-3 my-3">
            {icon}
            <h3 className="text-2xl font-semibold mb-3 text-center text-primary">
              {title}
            </h3>
          </div>
          <div className="text-center p-4">
            No {title.toLowerCase()} competencies available for this ability.
          </div>
        </div>
      );
    }

    return (
      <div
        className={`glassEffect rounded-lg shadow-lg p-4 z-50 ${isVertical ? "w-[420px]" : "w-[600px]"
          }`}
        style={gridStyles}
      >
        <div className="flex w-full justify-center gap-3 my-3">
          {icon}
          <h3 className="text-2xl font-semibold mb-3 text-center text-primary">
            {title}
          </h3>
        </div>

        <div
          className={`grid ${isVertical ? "grid-cols-2" : "grid-cols-4"} gap-2`}
        >
          {processedItems.map((item) => {
            console.log(`Rendering competency: ${item.id} - ${item.name || item.description}`);
            const isToggled = toggledCompetencies?.includes(item.id);
            return (
              <Tooltip placement="top" title={item.description || ""} overlayInnerStyle={{ fontSize: 10 }}>
                <motion.button
                  key={item.id}
                  onClick={(e) => handleCompetencyClick(item.id, e)}
                  className={`px-2 py-4 text-sm text-center rounded-lg border transition-colors flex items-start ${isToggled
                      ? "bg-accent text-primary border-2"
                      : "bg-white text-gray-700 border-gray-400 hover:border-primary border-2"
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.name || item.description || "No name available"}
                </motion.button>
              </Tooltip>

            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-96 w-96 flex overflow-hidden justify-center items-center">
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }

  return (
    <div
      className={`w-full h-full bg-northern-lights overflow-hidden ${className}`}
      onWheel={handleWheel}
    >
      <motion.div
        className="w-full h-full relative cursor-grab active:cursor-grabbing"
        drag
        dragMomentum={false}
        dragElastic={0}
        initial={{ x: -180, scale: 0.6 }}
        style={{ scale }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="relative w-[340px] h-[340px] ">
            <div className="h-[680px] w-1 bg-primary absolute top-[-50%] left-[50%]"></div>
            <div className="w-[680px] h-1 bg-primary absolute top-[50%] left-[-50%]"></div>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 404 404"
              className="absolute inset-0 w-full h-full z-0"
              preserveAspectRatio="xMidYMid meet"
            >
              <path
                stroke="none"
                fill={colours.primary}
                transform="rotate(90, 202, 202)"
                d="M194 6.6188021535171a16 16 0 0 1 16 0l157.20508075689 90.762395692966a16 16 0 0 1 8 13.856406460551l0 181.52479138593a16 16 0 0 1 -8 13.856406460551l-157.20508075689 90.762395692966a16 16 0 0 1 -16 0l-157.20508075689 -90.762395692966a16 16 0 0 1 -8 -13.856406460551l2.891072414886e-13 -181.52479138593a16 16 0 0 1 8 -13.856406460551"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <p className="text-center text-2xl font-semibold text-white w-[55%]">
                {currentAbility?.name}
              </p>
            </div>
          </div>

          <CategoryGrid
            title="Skills"
            items={competenciesByAbility?.skills || []}
            position="top"
            icon={<FaLightbulb className="text-3xl text-primary" />}
          />
          <CategoryGrid
            title="Knowledge"
            items={competenciesByAbility?.knowledge || []}
            position="right"
            icon={<FaBook className="text-3xl text-primary" />}
          />
          <CategoryGrid
            title="Mindset"
            items={competenciesByAbility?.mindset || []}
            position="bottom"
            icon={<FaBrain className="text-3xl text-primary" />}
          />
          <CategoryGrid
            title="Instruments"
            items={competenciesByAbility?.instruments || []}
            position="left"
            icon={<FaTools className="text-3xl text-primary" />}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default CompetencyMap;
